import React, { ComponentType, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useHistory, CompatHistory } from './useHistory';

/** v5-style `match` object. Only the fields the codebase actually reads are reconstructed. */
interface RouteMatch<Params = Record<string, string | undefined>> {
  params: Params;
  isExact: boolean;
  path: string;
  url: string;
}

/**
 * v5-style `RouteComponentProps`. react-router v6 removed this type (and the
 * `withRouter` HOC) because it favours hooks, but class components cannot use
 * hooks — so we reconstruct the injected props here.
 */
export interface RouteComponentProps<Params = Record<string, string | undefined>> {
  history: CompatHistory;
  location: Location;
  match: RouteMatch<Params>;
}

/**
 * Drop-in replacement for react-router v5's `withRouter`, backed by v6 hooks.
 * Injects `history` (a {@link CompatHistory}), `location`, and `match` — the
 * standard replacement pattern for class components under v6.
 */
// `any` for the params type: withRouter must accept components typed with any
// RouteComponentProps<Params> shape, so the constraint is intentionally permissive.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withRouter = <Props extends Partial<RouteComponentProps<any>>>(
  Component: ComponentType<Props>,
): React.FC<Omit<Props, keyof RouteComponentProps>> => {
  const WithRouter: React.FC<Omit<Props, keyof RouteComponentProps>> = (props) => {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();
    // Memoized so wrapped PureComponent/observer classes keep their
    // shallow-compare render bailouts (v5 injected stable references too).
    const match: RouteMatch = useMemo(
      () => ({
        params,
        isExact: true,
        path: location.pathname,
        url: location.pathname,
      }),
      [params, location.pathname],
    );

    return (
      <Component
        {...(props as unknown as Props)}
        history={history}
        location={location}
        match={match}
      />
    );
  };

  WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return WithRouter;
};
